import { DefaultPalette, Icon, IconButton, IContextualMenuProps, IIconProps, PrimaryButton, Text } from '@fluentui/react';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { routes } from '../../../../common/constants';
import MessageCourier from '../../../../lib/MessageCourier';
import { defaultCommandBarButtonStyles, titleCommandBarButtonStyles } from '../../../../styles/button-styles';
import TimeTaxSupportSystemClient from '../../../../system';
import ViewWrapperWithCommandBar from '../../../Layout/ViewWrapperWithCommandBar';
import { useTranslation } from 'react-i18next';
import DetalleLiquidaciónDirectaTable from './components/DetalleLiquidaciónDirectaTable';
import { LiquidacionAsincronaEstado, LiquidacionAsincronaV2Details } from '../../../../system/liquidacion_asincrona/LiquidacionAsincronaV2System';
import { DateTime } from 'luxon';
import { LiquidacionEstado } from './Wizard/LiquidacionEstado';
import { SettlementTablePreliquidadoAje } from './components/SettlementTablePreliquidadoAje';
import './detalle-liquidacion-aje.css';

interface IDirectSettlementDetailAje {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  changeBlockUI: (v: boolean) => void;
}

export interface ISettlementItemsGrouped {
  ldi_sector: string;
  ldi_periodo: string;
  promedio_costo_hora: string;
  ldi_horas_confirmadas: string;
  ldi_importe_costo_neto: string;
  ldi_importe_costo_total: string;
  concept: string;
}

const DirectSettlementDetailAje = (props: IDirectSettlementDetailAje) => {
  const { t } = useTranslation();
  const [settlement, setSettlement] = useState<LiquidacionAsincronaV2Details | null>(null);

  let { id } = useParams();
  const navigate = useNavigate();

  const GlobalNavButtonIcon: IIconProps = { iconName: 'GlobalNavButton' };

  const contextualMenuItems = [
    {
      key: 'accept',
      text: t('Aceptado'),
      onClick: () => {
        handleAcepted();
      },
      iconProps: { iconName: 'ActivateOrders' },
      className: settlement && settlement.liquidacion.lidasinc_estado === LiquidacionAsincronaEstado.PRELIQUIDADO ? '' : 'hide-project-btn',
    },
    {
      key: 'eliminate',
      text: t('Eliminado'),
      onClick: () => {
        handleEliminated();
      },
      iconProps: { iconName: 'DeactivateOrders' },
      className: settlement && [LiquidacionAsincronaEstado.PRELIQUIDADO, LiquidacionAsincronaEstado.PENDIENTE].includes(settlement.liquidacion.lidasinc_estado) ? '' : 'hide-project-btn',
    },
  ];

  const menuProps: IContextualMenuProps = {
    shouldFocusOnMount: true,
    items: contextualMenuItems,
  };

  const getSettlementResponse = async () => {
    if (id) {
      props.changeBlockUI(true);
      try {
        const settlement = await props.system.getLiquidacionAsincronaV2System().getById(id);
        if (settlement) {
          setSettlement(settlement.data);
        }
      } catch (error) {
        console.log(error);
        props.courier.messageError(t('Error al cargar la liquidación'));
      } finally {
        props.changeBlockUI(false);
      }
    }
  };
  const handleAcepted = async () => {
    if (id) {
      props.changeBlockUI(true);
      try {
        const response = await props.system.getLiquidacionAsincronaV2System().aceptar(id);
        if (response) {
          props.courier.messageSuccess(t('Liquidación aceptada correctamente'));
        }
      } catch (error) {
        console.log(error);
        props.courier.messageError(t('Error al aceptar la liquidación'));
      } finally {
        props.changeBlockUI(false);
        getSettlementResponse();
      }
    }
  };

  const handleEliminated = async () => {
    if (id) {
      props.changeBlockUI(true);
      try {
        const response = await props.system.getLiquidacionAsincronaV2System().eliminar(id);
        if (response) {
          props.courier.messageSuccess(t('Liquidación eliminada correctamente'));
        }
      } catch (error) {
        console.log(error);
        props.courier.messageError(t('Error al eliminar la liquidación'));
      } finally {
        props.changeBlockUI(false);
        getSettlementResponse();
      }
    }
  };
  const handleDownloadLiquidacion = async () => {
    if (!id) return;
    props.changeBlockUI(true);

    try {
      // Llamada al endpoint que ahora devuelve un ZIP
      const blob = await props.system.getLiquidacionAsincronaV2System().downloadLiquidacion(id);

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);

      link.download = `liquidacion_${id}.zip`;

      link.click();
      URL.revokeObjectURL(link.href);

      props.courier.messageSuccess(t('Liquidación descargada correctamente'));
    } catch (error) {
      console.log(error);
      props.courier.messageError(t('Error al descargar la liquidación'));
    } finally {
      props.changeBlockUI(false);
    }
  };

  const handleDownloadInsumos = async () => {
    if (!id) return;
    props.changeBlockUI(true);
    try {
      // Llamada al endpoint que ahora devuelve un ZIP
      const blob = await props.system.getLiquidacionAsincronaV2System().downloadInsumos(id);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `insumos_${id}.zip`;
      link.click();
      URL.revokeObjectURL(link.href);
      props.courier.messageSuccess(t('Insumos descargados correctamente'));
    } catch (error) {
      console.log(error);

      props.courier.messageError(t('Error al descargar los insumos'));
    } finally {
      props.changeBlockUI(false);
    }
  };

  useEffect(() => {
    if (id) {
      getSettlementResponse();
    }
  }, [id]);

  const itemsMenuCalendar = React.useMemo(
    (): ICommandBarItemProps[] => [
      {
        key: 'title',
        text: t('Liquidaciones directas'),
        iconProps: {
          iconName: 'ReminderPerson',
          color: DefaultPalette.themeLighterAlt,
          className: 'text-title-btn',
        },
        buttonStyles: titleCommandBarButtonStyles,
        className: 'text-title-btn',
      },
      {
        key: 'back',
        text: t('Volver'),
        iconProps: {
          iconName: 'Back',
          color: DefaultPalette.themeLighterAlt,
          className: 'text-title-btn',
        },
        buttonStyles: titleCommandBarButtonStyles,
        className: 'text-title-btn',
        onClick: () => {
          navigate(`${routes.direct_settlement_list_aje}`);
        },
      },
    ],
    [],
  );

  return (
    <ViewWrapperWithCommandBar
      withBorderBottom
      title={''}
      items={itemsMenuCalendar}
      overflowButtonStyles={defaultCommandBarButtonStyles}
      variant="highligth"
      titleBody={t('Liquidaciones')}
    >
      {settlement && (
        <div className="details-container-aje">
          <div className="details-content-aje">
            {/* HEADER */}
            <div className="details-header-aje-container">
              <div className="details-header-aje">
                <Text variant="large" className="fw-500">
                  {t('Detalle de liquidacion directa: ') + id}
                </Text>
                <div className="details-header-right-aje">
                  <LiquidacionEstado status={settlement.liquidacion.lidasinc_estado} />

                  {[LiquidacionAsincronaEstado.PRELIQUIDADO, LiquidacionAsincronaEstado.PENDIENTE].includes(settlement.liquidacion.lidasinc_estado) && (
                    <IconButton iconProps={GlobalNavButtonIcon} title="Opciones de actividad" ariaLabel="GlobalNavButton" menuProps={menuProps} />
                  )}
                </div>
              </div>

              {[LiquidacionAsincronaEstado.PRELIQUIDADO, LiquidacionAsincronaEstado.ACEPTADO].includes(settlement.liquidacion.lidasinc_estado) && (
                <div className="details-buttons-aje">
                  <PrimaryButton
                    text={t('Descargar liquidación')}
                    iconProps={{ iconName: 'ZipFolder', color: DefaultPalette.themeLighterAlt }}
                    onClick={handleDownloadLiquidacion}
                  />
                  <PrimaryButton
                    text={t('Descargar insumos')}
                    iconProps={{ iconName: 'ZipFolder', color: DefaultPalette.themeLighterAlt }}
                    onClick={handleDownloadInsumos}
                  />
                </div>
              )}
            </div>

            {/* 
              Si el estado de la liquidación es PENDIENTE se muestran la siguientes secciones:
              - Info del periodo y tipo de liquidación
              - Información importante para el usuario
              - Tabla con las equivalencias
             */}
            {settlement.liquidacion.lidasinc_estado === LiquidacionAsincronaEstado.PENDIENTE && (
              <>
                {/* INFO */}
                <div className="details-info-periodo-tipo">
                  <div className="detail-info-item-aje">
                    <span className="tts-liquidacion-label">{t('Periodo a liquidar')}:</span>
                    <span className="tts-liquidacion-value tts-liquidacion-value--padding">
                      {DateTime.fromFormat(settlement.liquidacion.lidasinc_periodo, 'yyyyMM').toFormat('MM/yyyy')}
                    </span>
                  </div>

                  <div className="detail-info-item-aje">
                    <span className="tts-liquidacion-label">{t('Tipo de liquidación')}:</span>
                    <span className="tts-liquidacion-value tts-liquidacion-value--padding">{settlement.liquidacion.lidasinc_tipo_liquidacion}</span>
                  </div>
                </div>

                {/* INFO 2 */}
                <div className="details-info-2-aje">
                  <Icon iconName="Info" className="text-icon" />
                  <ul className="p-0">
                    <li>{t("En este estado se visualizan las equivalencias no encontradas.")}</li>
                    <li>{t("Colocar en la columna “Detalle” el proyecto y/o servicio correcto.")}</li>
                    <li>{t("Colocar en la columna “Compañía” la información correcta.")}</li>
                    <li>{t("(*) Campo obligatorio")}</li>
                  </ul>
                </div>

                {/* TABLE */}

                <DetalleLiquidaciónDirectaTable
                  settlement={settlement}
                  system={props.system}
                  courier={props.courier}
                  changeBlockUI={props.changeBlockUI}
                  id={id!}
                />
              </>
            )}

            {[LiquidacionAsincronaEstado.PRELIQUIDADO, LiquidacionAsincronaEstado.ACEPTADO, LiquidacionAsincronaEstado.ELIMINADO].includes(
              settlement.liquidacion.lidasinc_estado,
            ) && (
              <>
                <div className="mb-2">
                  <div className="details-row-aje">
                    <Icon iconName="Calendar" className="details-icon-aje" />
                    <Text>
                      {/* <b>Periodo a liquidar:</b> <span className="details-highlight-aje">09/2025</span>
                      <span className="details-muted-aje"> [21/08/2025 - 20/09/2025]</span> */}
                      <b>{t("Periodo a liquidar:")}</b>{' '}
                      <span className="details-highlight-aje">
                        {DateTime.fromFormat(settlement.liquidacion.lidasinc_periodo, 'yyyyMM').toFormat('MM/yyyy')}
                      </span>
                      {/* <span className="details-muted-aje">
                        {' '}
                        [ {DateTime.fromFormat(settlement.liquidacion.lidasinc_periodo, 'yyyyMM').startOf('month').toFormat('dd/MM/yyyy')} -{' '}
                        {DateTime.fromFormat(settlement.liquidacion.lidasinc_periodo, 'yyyyMM').endOf('month').toFormat('dd/MM/yyyy')} ]
                      </span> */}
                    </Text>
                  </div>

                  <div className="details-row-aje">
                    <Icon iconName="CalendarSettings" className="details-icon-aje" />
                    <Text>
                      <b>{t("Tipo de liquidación:")}</b> {settlement.liquidacion.lidasinc_tipo_liquidacion || 'N/A'}
                    </Text>
                  </div>

                  {/* <div className="details-row-aje">
                    <Icon iconName="Money" className="details-icon-aje" />
                    <Text>
                      <b>Total liquidado:</b>{' '}
                      <span className="details-amount-aje">
                        {settlement.liquidacion.lidasinc_importe_total ? `S/. ${settlement.liquidacion.lidasinc_importe_total}` : 'N/A'}
                      </span>
                    </Text>
                  </div> */}

                  <div className="details-row-aje">
                    <Icon iconName="Trending12" className="details-icon-aje" />
                    <Text>
                      <b>{t("Estado:")}</b> <span className="details-status-aje">{settlement.liquidacion.lidasinc_estado.toLowerCase()}</span>
                    </Text>
                  </div>

                  <div className="details-row-aje">
                    <Icon iconName="EventDate" className="details-icon-aje" />
                    <Text>
                      <b>{t("Creada el:")}</b>{' '}
                      {DateTime.fromFormat(settlement.liquidacion.lidasinc_fecha_creacion, 'yyyy-MM-dd HH:mm:ss').toFormat('dd/MM/yyyy hh:mm:ss a')}
                    </Text>
                  </div>

                  <div className="details-row-aje">
                    <Icon iconName="Contact" className="details-icon-aje" />
                    <Text>
                      <b>{t("Generado por:")}</b> {settlement.liquidacion.lidasinc_col_nombres_completos || 'N/A'}
                    </Text>
                  </div>

                  {settlement.liquidacion.lidasinc_estado === LiquidacionAsincronaEstado.ACEPTADO && (
                    <div className="details-row-aje">
                      <Icon iconName="Contact" className="details-icon-aje" />
                      <Text>
                        <b>{t("Aceptado por:")}</b> {settlement.liquidacion.lidasinc_aceptado_col_nombres_completos || 'N/A'}
                      </Text>
                    </div>
                  )}

                  {settlement.liquidacion.lidasinc_estado === LiquidacionAsincronaEstado.ELIMINADO && (
                    <div className="details-row-aje">
                      <Icon iconName="Contact" className="details-icon-aje" />
                      <Text>
                        <b>{t("Eliminado por:")}</b> {settlement.liquidacion.lidasinc_eliminado_col_nombres_completos || 'N/A'}
                      </Text>
                    </div>
                  )}
                </div>

                <SettlementTablePreliquidadoAje system={props.system} courier={props.courier} changeBlockUI={props.changeBlockUI} id={id!} />
              </>
            )}
          </div>
        </div>
      )}
    </ViewWrapperWithCommandBar>
  );
};

export default DirectSettlementDetailAje;
